// admin/src/components/batch/detail/BatchJobDetailPanel.tsx
import {FC, useState} from 'react';
import {Alert, Card, CardContent, Stack, TablePagination, Typography} from '@mui/material';
import {AdminBatchJobDetail, ExecutionMode} from '../../../types/adminBatch.ts';
import {batchJobStatusLabel, batchJobTypeLabel, batchScopeLabel, formatBatchDateTime} from '../../../utils/batchUi.ts';
import {getJobGroup} from '../batchJobTypeGroups.ts';
import {JobActionBar} from '../actions/JobActionBar.tsx';
import {SubmissionTable} from '../submission/SubmissionTable.tsx';
import {BackfillJobDetail} from './BackfillJobDetail.tsx';
import {QualityJobDetail} from './QualityJobDetail.tsx';
import {
    useCancelAdminBatchSubmission,
    useRecheckAdminBatchSubmissionNow,
    useReprocessAllCached,
    useReprocessCachedSubmission,
    useResolveStaleAdminBatchSubmissions,
    useRetryAdminBatchSubmission,
    useRetryAdminBatchSubmissionNextProvider,
    useRetryFailedAdminBatchSubmissions,
} from '../../../hooks/useAdminBatch.ts';

interface BatchJobDetailPanelProps {
    detail: AdminBatchJobDetail;
    onRefresh: () => void;
    onRefreshList: () => void;
    submissionPage: number;
    submissionSize: number;
    onSubmissionPageChange: (page: number) => void;
    onSubmissionSizeChange: (size: number) => void;
    generatedPage: number;
    generatedSize: number;
    onGeneratedPageChange: (page: number) => void;
    onGeneratedSizeChange: (size: number) => void;
}

type BatchResultInfo = {
    phase?: string;
    currentProvider?: string;
    heartbeatAt?: string;
    failureSummary?: {
        providerFailedBatches?: number;
        internalFailedBatches?: number;
        retrySuppressedBatches?: number;
        retriedWithFallbackBatches?: number;
    };
};

const parseResultJson = (resultJson?: string): BatchResultInfo => {
    if (!resultJson) return {};
    try {
        const parsed = JSON.parse(resultJson) as BatchResultInfo;
        return {phase: parsed.phase, currentProvider: parsed.currentProvider, heartbeatAt: parsed.heartbeatAt, failureSummary: parsed.failureSummary};
    } catch { return {}; }
};


export const BatchJobDetailPanel: FC<BatchJobDetailPanelProps> = ({
    detail, onRefresh, onRefreshList,
    submissionPage, submissionSize, onSubmissionPageChange, onSubmissionSizeChange,
}) => {
    const {job, submissions = []} = detail;
    const group = getJobGroup(job.jobType);
    const result = parseResultJson(job.resultJson);
    const [actionError, setActionError] = useState<string | null>(null);

    // mutation hooks
    const cancelSubmission = useCancelAdminBatchSubmission();
    const recheckNow = useRecheckAdminBatchSubmissionNow();
    const retryNextProvider = useRetryAdminBatchSubmissionNextProvider();
    const retryWithMode = useRetryAdminBatchSubmission();
    const reprocessCached = useReprocessCachedSubmission();
    const reprocessAllCached = useReprocessAllCached();
    const resolveStale = useResolveStaleAdminBatchSubmissions();
    const retryFailed = useRetryFailedAdminBatchSubmissions();

    const isPending = cancelSubmission.isPending || recheckNow.isPending || retryNextProvider.isPending
        || retryWithMode.isPending || reprocessCached.isPending
        || reprocessAllCached.isPending || resolveStale.isPending || retryFailed.isPending;

    const refreshAll = () => { onRefresh(); onRefreshList(); };

    // submission action handlers
    const handleRecheck = (jobId: number, submissionId: number) => {
        setActionError(null);
        recheckNow.mutate({jobId, submissionId}, {
            onSuccess: (res) => { alert(`재확인 완료: external=${res.externalStatus ?? '-'}, status=${res.previousStatus} -> ${res.currentStatus}`); refreshAll(); },
            onError: (e) => setActionError(e.message),
        });
    };
    const handleCancel = (jobId: number, submissionId: number) => {
        setActionError(null);
        cancelSubmission.mutate({jobId, submissionId}, {onSuccess: refreshAll, onError: (e) => setActionError(e.message)});
    };
    const handleNextProvider = (jobId: number, submissionId: number) => {
        setActionError(null);
        retryNextProvider.mutate({jobId, submissionId}, {onSuccess: refreshAll, onError: (e) => setActionError(e.message)});
    };
    const handleRetryQuiz = (jobId: number, submissionId: number, executionMode: ExecutionMode) => {
        setActionError(null);
        retryWithMode.mutate({jobId, submissionId, executionMode}, {
            onSuccess: () => { alert('재시도 submission 생성됨'); onRefresh(); },
            onError: (e) => setActionError(e.message),
        });
    };
    const handleReprocessCached = (jobId: number, submissionId: number) => {
        setActionError(null);
        reprocessCached.mutate({jobId, submissionId}, {
            onSuccess: (res) => {
                alert(res.errorMessage ? `재처리 실패: ${res.errorMessage}` : `재처리 완료: generated=${res.generated}, skipped=${res.skipped}, failed=${res.failed}`);
                refreshAll();
            },
            onError: (e) => setActionError(e.message),
        });
    };

    // job action handlers
    const handleResolveStale = (jobId: number, staleMinutes: number, action: 'CANCEL' | 'RETRY_NEXT_PROVIDER') => {
        resolveStale.mutate({jobId, staleMinutes, action}, {
            onSuccess: (res) => { alert(`정리 완료: matched=${res.matched}, canceled=${res.canceled}, retried=${res.retried}, pendingRemaining=${res.pendingRemaining}`); refreshAll(); },
        });
    };
    const handleRetryFailed = (jobId: number, executionMode: ExecutionMode) => {
        retryFailed.mutate({jobId, executionMode}, {
            onSuccess: (res) => { alert(`재시도 submission ${res.created}개 생성됨 (${res.executionMode})`); refreshAll(); },
        });
    };
    const handleReprocessAllCached = (jobId: number) => {
        setActionError(null);
        reprocessAllCached.mutate({jobId}, {
            onSuccess: (res) => {
                alert(`전체 캐시 재처리 완료\n\n전체 submission: ${res.totalSubmissions}\n처리: ${res.processed}\n신규 생성: ${res.newGenerated}\n스킵: ${res.skipped}\n실패: ${res.failed}\n캐시 없음: ${res.noCache}`);
                refreshAll();
            },
            onError: (e) => setActionError(`캐시 재처리 실패: ${e instanceof Error ? e.message : String(e)}`),
        });
    };
    // group-specific detail
    const renderGroupDetail = () => {
        switch (group) {
            case 'BACKFILL':
                return job.jobType === 'DICTIONARY_METADATA_BACKFILL' ? <BackfillJobDetail submissions={submissions} /> : null;
            case 'QUALITY':
                return <QualityJobDetail job={job} />;
        }
    };

    return (
        <Stack spacing={2}>
            {/* 공통 Job 정보 헤더 */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6">
                        #{job.id}: {batchJobTypeLabel(job.jobType)} ({batchScopeLabel(job)})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        상태: {batchJobStatusLabel(job.status)} / 처리: {job.processedCount}/{job.targetCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        시작: {formatBatchDateTime(job.startedAt)} / 종료: {formatBatchDateTime(job.finishedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        요청/대상/처리: {job.requestedLimit}/{job.targetCount}/{job.processedCount}
                        {' · '}
                        성공/스킵/실패: {job.successCount}/{job.skippedCount}/{job.failedCount}
                    </Typography>
                    {result.phase && (
                        <Stack direction="row" spacing={1} sx={{mt: 0.5}}>
                            <Typography variant="caption">{result.phase}</Typography>
                            <Typography variant="caption" color="text.secondary">{result.currentProvider || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatBatchDateTime(result.heartbeatAt)}</Typography>
                            {result.failureSummary && (
                                <Typography variant="caption" color="text.secondary">
                                    P:{result.failureSummary.providerFailedBatches ?? 0}
                                    {' / '}I:{result.failureSummary.internalFailedBatches ?? 0}
                                    {' / '}Retry:{result.failureSummary.retriedWithFallbackBatches ?? 0}
                                    {' / '}Stop:{result.failureSummary.retrySuppressedBatches ?? 0}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    {job.errorMessage && <Alert severity="warning" sx={{mt: 1}}>{job.errorMessage}</Alert>}

                    <JobActionBar
                        job={job} submissions={submissions}
                        onResolveStale={handleResolveStale} onRetryFailed={handleRetryFailed}
                        onReprocessAllCached={handleReprocessAllCached}
                        isPending={isPending}
                    />
                </CardContent>
            </Card>

            {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}

            {/* 그룹별 결과 요약 */}
            {renderGroupDetail()}

            {/* 공통 Submission 테이블 */}
            <Card variant="outlined">
                <CardContent sx={{p: 0}}>
                    <SubmissionTable
                        submissions={submissions} jobId={job.id} jobType={job.jobType} totalSteps={job.targetCount}
                        onRecheck={handleRecheck} onCancel={handleCancel} onNextProvider={handleNextProvider}
                        onRetryQuiz={handleRetryQuiz} onReprocessCached={handleReprocessCached}
                        isPending={isPending}
                    />
                    <TablePagination
                        component="div"
                        count={detail.totalSubmissionCount ?? submissions.length}
                        page={submissionPage}
                        onPageChange={(_, p) => onSubmissionPageChange(p)}
                        rowsPerPage={submissionSize}
                        onRowsPerPageChange={(e) => { onSubmissionSizeChange(parseInt(e.target.value, 10)); onSubmissionPageChange(0); }}
                        rowsPerPageOptions={[50, 100, 200]}
                    />
                </CardContent>
            </Card>
        </Stack>
    );
};
